import { useRef, useMemo, forwardRef } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Icon from "src/components/Display/Icon/Icon";
import getColorLuma from 'src/utils/colors/getColorLuma.js';

const Badge = forwardRef((props, ref) => {
  const {
    id,
    className,
    color,
    text,
    badgeAriaLabel = 'Badge',
    dataTestId,
    shape = 'circle',
    onClick,
    type,
    icon,
  } = props;

  if (text === '' || text === undefined) {
    return null;
  }

  const internalRef = useRef(null);
  const badgeRef = ref || internalRef;

  const luma = getColorLuma(color);
  const textColor = luma < 128 ? 'white' : '#4A657F';
  
  const style = {
    color: textColor,
    backgroundColor: props.color,
    ...props.style,
  };
  

  const badgeBaseClass = 'min-w-6 text-center box-border inline-block text-xs p-1';

  const badgeClass = useMemo(() => {
    const baseClass = shape === 'square' ? 'leading-7 h-10 rounded-none font-bold px-2' : 'leading-3 h-6 rounded-xl';
    return classNames(
      badgeBaseClass,
      baseClass,
      {
        'border-solid border border-brand-blue rounded-6 h-6': type === 'filter',
      },
      className,
    );
  }, [shape, className, type]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (onClick) {
        onClick(e);
      }
    } else if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
    }
  };

  const iconElement = icon?.name ? (
    <Icon
      color={icon?.color}
      icon={icon.name}
      style={icon?.style}
      className={icon?.className}
    />
  ) : null;

  return (
    <span
      id={id}
      className={badgeClass}
      style={style}
      aria-label={`${badgeAriaLabel}: ${text}`}
      tabIndex={0}
      role="button"
      ref={badgeRef}
      onKeyDown={handleKeyDown}
      data-testid={dataTestId}
      onClick={onClick}
    >
      {iconElement && icon?.position === "left" && <span className="mr-1">{iconElement}</span>}
      {text}
      {iconElement && icon?.position === "right" && <span className="ml-1">{iconElement}</span>}
    </span>
  );
});

Badge.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  color: PropTypes.string,
  text: PropTypes.string,
  badgeAriaLabel: PropTypes.string,
  dataTestId: PropTypes.string,
  shape: PropTypes.oneOf(['square', 'circle']),
  onClick: PropTypes.func,
  type: PropTypes.oneOf(['filter']),
  icon: PropTypes.shape({
    name: PropTypes.string,
    color: PropTypes.string,
    style: PropTypes.string,
    position: PropTypes.oneOf(['left', 'right']),
    className: PropTypes.string,
  }),
};

export default Badge;